import type { InputHandlerDeps } from "./handlers/types.ts";
import {
  PHASE_BUILD,
  PHASE_COMBAT,
  PHASE_GAME_OVER,
  PHASE_PAUSED,
} from "./types.ts";
import {
  enterPause,
  exitPause,
  setSelectedTower,
  setHoveredTower as setHoveredTowerState,
  setPlacementMode,
  setSelectedTowerType,
  clearPayloadTargeting,
  setWaveCountdown,
  setWaveStartedThisCountdown,
  setTimeWarpAvailable,
} from "./stateIntents.ts";
import type { Vec2 } from "../core/types.ts";
import { CellType } from "../core/types.ts";
import { GRID_WIDTH, GRID_HEIGHT, CELL_SIZE } from "../core/config.ts";
import { COLORS } from "../core/constants.ts";
import { Firewall } from "../entities/Firewall.ts";
import type { Element } from "../entities/elements.ts";
import { rerouteAllPaths } from "../systems/PathRerouter.ts";
import { t } from "../utils/i18n.ts";
import { createPreviewPort, createPlacePort } from "../input/placementPorts.ts";
import { createArmedProvider } from "../input/PlaceableDescriptor.ts";
import { bindPlacementPointer } from "../input/bindPlacementPointer.ts";

/** Pre-game menu overlays (pre-run, boot) own the keyboard while visible.
 *  Without this gate the global game keys (ESC→pause, S→skills, Space→wave)
 *  fire underneath the menu and corrupt run state — e.g. ESC on the pre-run
 *  screen would flip gs.phase to PAUSED and show the pause overlay. */
function isPreGameMenuOpen(): boolean {
  for (const id of ["pre-run-screen", "boot-sequence"]) {
    const el = document.getElementById(id);
    if (el && el.style.display !== "none") return true;
  }
  return false;
}

/** F-12 — the ONE shared "a modal/overlay owns input" predicate. Without this the
 *  document keydown handler only bailed for PHASE_GAME_OVER and `isPreGameMenuOpen()`
 *  (pre-run/boot only), so the THREAT ARCHIVE, the ⚙ gear dropdown, the skill
 *  overview, and the legal overlay all left the game keys reachable underneath them:
 *  1-7 could arm a placeable, Space could start a wave, S/T/M/F could fire — while a
 *  modal sat on top eating the visual focus. Checked ONCE, generically, so no
 *  per-overlay special case has to be bolted onto the switch below:
 *   - PAUSED covers both the explicit ESC-pause AND the ⚙ gear dropdown, which
 *     ITSELF forces PHASE_PAUSED while open (`HUD.onGearOpen`) — no separate gear
 *     check needed.
 *   - `skillOverview.visible` / `enemyLibrary.visible` cover the two content
 *     overlays that do NOT touch `gs.phase`.
 *   - The legal overlay is reachable only from the pre-run screen today (already
 *     caught by `isPreGameMenuOpen()`), but it is checked here too via its DOM
 *     marker class so this predicate stays correct even if a future surface opens
 *     it mid-run — no new dependency needed, mirroring `isPreGameMenuOpen`'s own
 *     DOM-query idiom.
 *  Escape is deliberately NOT gated by this — every one of these surfaces already
 *  owns its own Escape close/exit path (the switch below, or the overlay's own
 *  capture-phase listener), and gating Escape too would make it impossible to
 *  close any of them. */
function isOverlayBlockingInput(
  gs: { phase: string },
  skillOverview: { visible: boolean },
  enemyLibrary?: { visible: boolean },
): boolean {
  if (gs.phase === PHASE_PAUSED) return true;
  if (skillOverview.visible) return true;
  if (enemyLibrary?.visible) return true;
  if (document.querySelector(".legal-overlay")) return true;
  return false;
}

export interface MouseState {
  col: number;
  row: number;
  valid: boolean;
  canBuild: boolean;
  /** CR-P2 (Bug C): preview paths for ALL active entries (primary + breach
   *  secondaries), not just primary. Renderer iterates this list. null when
   *  no placement preview active. canBuild is true only if every entry can
   *  still reach an exit after the hypothetical placement (mirrors canPlace). */
  previewPaths: Vec2[][] | null;
  /** BL-75: the aimed cell is eligible + affordable but would SEAL the only route
   *  (canPlaceAt's reachability failed) — drives the distinct "SEALS PATH" warning.
   *  Optional so legacy MouseState constructors stay valid; defaults falsy. */
  sealsPath?: boolean;
}

/** Setup all input handlers (pointer + keyboard). Returns mouse state for render. */
export function setupInputHandlers(deps: InputHandlerDeps): MouseState {
  const {
    gs,
    canvas,
    grid,
    pathfinding,
    economy,
    towerData,
    waveManager,
    synergySystem,
    combatSystem,
    towerSelector,
    eventBus,
    skillOverview,
    draftSystem,
    hud,
    sellDialog,
    recalculatePath,
    pathRegistry,
    spawnDetectionSystem,
    gameBanner,
    enemyLibrary,
  } = deps;

  const mouse: MouseState = {
    col: -1,
    row: -1,
    valid: false,
    canBuild: false,
    previewPaths: null,
    sealsPath: false,
  };

  // BL-75: the preview / commit / validity logic lives behind injected ports (PreviewPort /
  // PlacePort) + a per-placeable descriptor — the SAME implementation the DOM-agnostic
  // PlacementController drives. Reused, not rewritten → the headless sim baseline is unchanged.
  const preview = createPreviewPort(mouse, pathRegistry);
  const placePort = createPlacePort({
    gs,
    grid,
    economy,
    synergySystem,
    spawnDetectionSystem,
    waveManager,
    pathRegistry,
    eventBus,
    recalculatePath,
  });
  const getArmed = createArmedProvider({
    gs,
    towerData,
    grid,
    pathfinding,
    economy,
    placePort,
  });

  const sellFirewall = (fw: Firewall): void => {
    const fwIdx = gs.firewalls.findIndex((f) => f === fw);
    if (fwIdx === -1) return;
    gs.firewalls.splice(fwIdx, 1);
    grid.setCell(fw.col, fw.row, CellType.EMPTY);
    economy.addGold(fw.cost);
    recalculatePath();
    waveManager.pathPixels = gs.pathPixels;
    rerouteAllPaths(waveManager, pathRegistry);
    eventBus.emit("firewall.sold", {
      firewall: fw,
      sellPrice: fw.cost,
    });
  };

  const showFirewallSellDialog = (fw: Firewall): void => {
    if (sellDialog.isVisible()) return;
    sellDialog.show({
      title: t("sell.firewallTitle"),
      itemName: "FIREWALL",
      invested: fw.cost,
      refund: fw.cost,
      refundPercent: 100,
      onConfirm: () => sellFirewall(fw),
      onCancel: () => {},
    });
  };

  // ── The non-placement surface the deleted `click` also owned (SPEC §1.5) ──
  // A board press routes here first; if it consumes the interaction (select a tower, sell a
  // firewall, or the PT-17 edit-mode deselect) it returns true and the controller does NOT
  // carry. This is the existing `click` logic, unchanged — just reachable from the controller.
  const tryHandleNonPlacement = (col: number, row: number): boolean => {
    const existing = gs.towers.find((tw) => tw.col === col && tw.row === row);
    if (existing) {
      // Toggle: press the same tower again → deselect; else select (range + HUD panel).
      setSelectedTower(gs, existing === gs.selectedTower ? null : existing);
      return true;
    }
    const existingFirewall = gs.firewalls.find(
      (fw) => fw.col === col && fw.row === row,
    );
    if (existingFirewall) {
      setSelectedTower(gs, null);
      showFirewallSellDialog(existingFirewall);
      return true;
    }
    // PT-17: the first empty-cell interaction while a tower is selected deselects WITHOUT
    // placing (edit-mode exit). A subsequent press (now deselected) builds as usual.
    if (gs.selectedTower != null) {
      setSelectedTower(gs, null);
      return true;
    }
    return false;
  };

  // CR-28: mouse-only hovered-tower range indicator (preserved from the old `mousemove`).
  const setHoveredTower = (col: number, row: number): void => {
    setHoveredTowerState(
      gs,
      gs.towers.find((tw) => tw.col === col && tw.row === row) ?? null,
    );
  };
  const clearHoveredTower = (): void => {
    setHoveredTowerState(gs, null);
  };

  // Arm from a chip's `data-type` — the SAME gs source of truth the number keys write
  // (no parallel `armed` field). Routed through `towerSelector.select()` (not the bare
  // `selectedType` setter) so a chip-driven arm gets the FULL selection contract (the
  // `onSelect` callback → gs mutation + `firstRunCoach.advanceToPlace()`) through this
  // ONE path (F-1/F-2/F-3: the pointer pipeline is now the SOLE arming surface — the
  // chip's own `click` listener no longer arms, see `TowerSelector.ts`).
  //
  // F-5 (corrected, D-332 amendment) — a TAP on the ALREADY-armed chip TOGGLES it off
  // (disarm); a DRAG started from the already-armed chip must NOT disarm (it needs to
  // stay armed so the drag's ghost/commit works at all). `armChip` fires at POINTERDOWN,
  // before tap-vs-drag is knowable, so the toggle decision can no longer live here — it
  // is now made by `PlacementController.onUp` (tap vs drag is known by then), using
  // `isChipArmed` (read BEFORE the DOWN-time arm) + `disarmPlaceable` below.
  // `armPlaceable` itself is now a plain, idempotent arm — always selects, never toggles.
  const armPlaceable = (type: string): void => {
    towerSelector.select(type as Element | "firewall");
  };
  const isChipArmed = (type: string): boolean =>
    type === "firewall"
      ? gs.placementMode === "firewall"
      : gs.placementMode === "tower" && gs.selectedTowerType === type;
  const disarmPlaceable = (): void => {
    towerSelector.select(null);
  };

  const isInteractive = (): boolean =>
    gs.lives > 0 &&
    (gs.phase === PHASE_BUILD || gs.phase === PHASE_COMBAT);

  const payloadActive = (): boolean =>
    gs.payloadTargetingMode != null && deps.deployTargetedPayload != null;

  // BL-70: the existing payload tap-to-target resolution (Phase-2 descriptor conversion is
  // out of scope) — moved verbatim onto the pointer-up tap.
  const resolvePayload = (clientX: number, clientY: number): void => {
    if (!gs.payloadTargetingMode || !deps.deployTargetedPayload) return;
    const rect = canvas.getBoundingClientRect();
    const scaleX = rect.width / (GRID_WIDTH * CELL_SIZE);
    const x = (clientX - rect.left) / scaleX;
    const y = (clientY - rect.top) / scaleX;

    if (gs.payloadTargetingMode === "targetCell") {
      const col = Math.floor(x / CELL_SIZE);
      const row = Math.floor(y / CELL_SIZE);
      if (col >= 0 && col < GRID_WIDTH && row >= 0 && row < GRID_HEIGHT) {
        deps.deployTargetedPayload({ col, row });
      }
    } else if (gs.payloadTargetingMode === "targetEnemy") {
      const enemies = waveManager.enemies;
      let bestIdx = -1;
      let bestDist = CELL_SIZE; // max hit radius
      for (let i = 0; i < enemies.length; i++) {
        const en = enemies[i]!;
        if (!en.active) continue;
        const dx = en.x - x;
        const dy = en.y - y;
        const dist = Math.sqrt(dx * dx + dy * dy);
        if (dist < bestDist) {
          bestDist = dist;
          bestIdx = i;
        }
      }
      if (bestIdx >= 0) {
        deps.deployTargetedPayload({ enemyIndex: bestIdx });
      }
      // No target within range → do nothing (don't consume the tap).
    }
  };

  // ── The unified pointer pipeline (REPLACES the legacy canvas mousemove/mouseleave/click) ──
  const placementController = bindPlacementPointer({
    canvas,
    mouse,
    preview,
    getArmed,
    tryHandleNonPlacement,
    setHoveredTower,
    clearHoveredTower,
    arm: armPlaceable,
    isArmed: isChipArmed,
    disarm: disarmPlaceable,
    isInteractive,
    payloadActive,
    resolvePayload,
  });

  document.addEventListener("keydown", (e) => {
    if (gs.phase === PHASE_GAME_OVER) return;
    if (isPreGameMenuOpen()) return;

    // BL-70: Cancel targeting on ESC / any control key → re-arm the payload.
    if (gs.payloadTargetingMode && e.code === "Escape") {
      clearPayloadTargeting(gs);
      eventBus.emit("payload.targetingCancelled");
      return;
    }

    // BL-75: Escape cancels an in-flight carry FIRST (coexists with the pause/deselect Esc
    // below — it only fires while actively dragging a placeable).
    if (e.code === "Escape" && placementController.isCarrying()) {
      placementController.onCancel();
      return;
    }

    // F-12: every overlay-owned surface (pause / gear / archive / skill-overview /
    // legal) blocks every OTHER key generically — see `isOverlayBlockingInput`.
    // Escape is exempt: each of those surfaces owns its own close/exit path below.
    if (
      e.code !== "Escape" &&
      isOverlayBlockingInput(gs, skillOverview, enemyLibrary)
    ) {
      return;
    }

    switch (e.code) {
      case "Space":
        e.preventDefault();
        if (!gs.waveActive && gs.phase === PHASE_BUILD) {
          // CR-10: Early start bonus if countdown is running
          if (gs.waveCountdown > 0) {
            const bonus = 25 + gs.wave * 5;
            economy.addGold(bonus);
            setWaveCountdown(gs, 0);
            gameBanner.show(
              t("countdown.earlyBonus", { value: bonus }),
              COLORS.AMBER_WARN,
              1200,
            );
          }
          // Same guard renderPipeline uses; prevents auto-branch from
          // re-firing on the next frame if wave.started somehow doesn't
          // flip phase to PHASE_COMBAT.
          setWaveStartedThisCountdown(gs, true);
          eventBus.emit("wave.manualStart");
          waveManager.startWave();
        }
        break;
      case "Digit1":
        clearPayloadTargeting(gs);
        setSelectedTowerType(gs, "fire");
        setPlacementMode(gs, "tower");
        towerSelector.selectedType = "fire";
        break;
      case "Digit2":
        clearPayloadTargeting(gs);
        setSelectedTowerType(gs, "water");
        setPlacementMode(gs, "tower");
        towerSelector.selectedType = "water";
        break;
      case "Digit3":
        clearPayloadTargeting(gs);
        setSelectedTowerType(gs, "lightning");
        setPlacementMode(gs, "tower");
        towerSelector.selectedType = "lightning";
        break;
      case "Digit4":
        clearPayloadTargeting(gs);
        setSelectedTowerType(gs, "signal");
        setPlacementMode(gs, "tower");
        towerSelector.selectedType = "signal";
        break;
      case "Digit5":
        clearPayloadTargeting(gs);
        setSelectedTowerType(gs, "kinetic");
        setPlacementMode(gs, "tower");
        towerSelector.selectedType = "kinetic";
        break;
      case "Digit6":
        // F-4: the chip labelled "6" is VIRUS (ELEMENTS[5] — TowerSelector.ts's
        // `TOWER_KEYS` are 1-6 for the 6 towers incl. virus, 7 for firewall); this
        // key previously armed firewall instead, leaving virus with no key at all
        // and the advertised "7" doing nothing.
        clearPayloadTargeting(gs);
        setSelectedTowerType(gs, "virus");
        setPlacementMode(gs, "tower");
        towerSelector.selectedType = "virus";
        break;
      case "Digit7":
        clearPayloadTargeting(gs);
        setPlacementMode(gs, "firewall");
        towerSelector.selectedType = "firewall";
        break;
      case "KeyT":
        if (gs.timeWarpAvailable && gs.waveActive) {
          setTimeWarpAvailable(gs, false);
          // GA-40: seconds → milliseconds, see CombatSystem.activateTimeWarp.
          combatSystem.activateTimeWarp(gs.timeWarpDuration * 1000);
          eventBus.emit("game.timeWarpChanged", false);
        }
        break;
      case "Escape":
        if (gs.phase === PHASE_PAUSED) {
          exitPause(gs);
          eventBus.emit("game.resumed");
        } else if (gs.selectedTower) {
          setSelectedTower(gs, null);
        } else if (skillOverview.visible) {
          // Normal mode closes via ✕ CLOSE (#so-close); swap mode (BL-118) has
          // no header ✕ — ESC takes the always-safe KEEP DECK (#so-keep) instead.
          (
            (document.getElementById("so-close") ??
              document.getElementById("so-keep")) as HTMLElement | null
          )?.click();
        } else if (
          gs.placementMode === "firewall" ||
          gs.selectedTowerType != null
        ) {
          // F-5: an ARMED-but-not-carrying placeable previously had NO reachable
          // disarm at all — Escape fell straight through to `enterPause` (there was
          // no user-visible way to cancel a selection short of pausing the run).
          // Escape now disarms FIRST; pause only fires when nothing is armed.
          setPlacementMode(gs, "tower");
          setSelectedTowerType(gs, null);
          towerSelector.selectedType = null;
        } else if (gs.phase === PHASE_BUILD || gs.phase === PHASE_COMBAT) {
          enterPause(gs);
          eventBus.emit("game.paused");
        }
        break;
      case "KeyS":
        if (!skillOverview.visible && gs.phase === PHASE_BUILD) {
          skillOverview.show([...draftSystem.cards], gs.wave, null);
        }
        break;
      case "KeyM":
        eventBus.emit("audio.toggleMute");
        break;
      case "KeyF":
        if (gs.waveActive) hud.toggleFastForward();
        break;
    }
  });

  // Click outside canvas/right-panel deselects tower (orthogonal — never commits a placement).
  document.addEventListener("click", (e) => {
    const target = e.target as HTMLElement;
    if (
      !canvas.contains(target) &&
      !target.closest("#tower-row") &&
      !target.closest(".tower-info-panel")
    ) {
      setSelectedTower(gs, null);
    }
  });

  return mouse;
}
